import {
  formatPlanDelta,
  formatPlanHours,
  formatPlanMoney,
  formatPlanPercent,
} from "@/lib/plan-format";
import { cn } from "@/lib/utils";
import type { PlanActualsView } from "@/schemas/plan-actuals";

/** Tone for a variance where going UP is bad (hours, cost) or good (revenue, margin). */
export function varianceTone(
  value: string | null | undefined,
  upIs: "good" | "bad",
): string | undefined {
  if (value == null || Number(value) === 0) return undefined;
  const up = !value.startsWith("-");
  const good = up === (upIs === "good");
  return good ? "text-success-700 dark:text-success-400" : "text-warning-700 dark:text-warning-400";
}

interface Tile {
  label: string;
  planned: string;
  actual: string;
  variance: string;
  varianceClass?: string;
  title: string;
}

/**
 * Plan totals to date: planned, actual and the variance for hours, revenue, cost (cost
 * viewers only) and margin — the summary level of the comparison (spec R4, AC4, AC5).
 */
export function PlanActualsSummary({ view }: { view: PlanActualsView }) {
  const s = view.summary;
  const tiles: Tile[] = [
    {
      label: "Hours",
      planned: formatPlanHours(s.plannedHours),
      actual: formatPlanHours(s.actualHours),
      variance: `${formatPlanDelta(s.hoursVariance)} h · ${formatPlanPercent(s.hoursVariancePercent, { signed: true })}`,
      title: `Actual hours logged in NOS against planned effective hours, for the months to date. ${formatPlanHours(s.actualBillableHours)} of the actual hours are billable. NOS time arrives by week, and a week counts in the month it starts in.`,
    },
    {
      label: "Revenue",
      planned: formatPlanMoney(s.plannedRevenue),
      actual: formatPlanMoney(s.actualRevenue),
      variance: `${formatPlanDelta(s.revenueVariance, "money")} · ${formatPlanPercent(s.revenueVariancePercent, { signed: true })}`,
      varianceClass: varianceTone(s.revenueVariance, "good"),
      title: "Actual billable hours × the plan's bill rates.",
    },
    ...(s.plannedCost !== undefined && s.actualCost !== undefined
      ? [
          {
            label: "Cost",
            planned: formatPlanMoney(s.plannedCost),
            actual: formatPlanMoney(s.actualCost),
            variance: `${formatPlanDelta(s.costVariance, "money")} · ${formatPlanPercent(s.costVariancePercent, { signed: true })}`,
            varianceClass: varianceTone(s.costVariance, "bad"),
            title: "All actual hours × each Labor Category's internal cost.",
          },
        ]
      : []),
    {
      label: "Indicative Margin",
      planned: formatPlanPercent(s.plannedMarginPercent),
      actual: formatPlanPercent(s.actualMarginPercent),
      variance: formatPlanPercent(s.marginVariancePoints, { signed: true, unit: "pts" }),
      varianceClass: varianceTone(s.marginVariancePoints, "good"),
      title:
        "(Revenue − cost) ÷ revenue, planned and actual, with revenue at the plan's own bill rates. Indicative: a Contracted Fee or discount is not applied, so this can differ from the plan's Margin of record.",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="cursor-help rounded-md border border-border bg-card px-4 py-3"
            title={tile.title}
          >
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {tile.label}
            </div>
            <div className="mt-1 flex items-baseline justify-between gap-3 tabular-nums">
              <div>
                <div className="text-xs text-muted-foreground">Planned</div>
                <div className="font-display text-lg font-bold leading-tight">{tile.planned}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Actual</div>
                <div className="font-display text-lg font-bold leading-tight">{tile.actual}</div>
              </div>
            </div>
            <div className={cn("mt-1 text-sm font-medium tabular-nums", tile.varianceClass)}>
              {tile.variance}
            </div>
          </div>
        ))}
      </div>

      <div className="text-xs text-muted-foreground">
        Whole plan as planned: {formatPlanHours(s.fullPlanHours)} h ·{" "}
        {formatPlanMoney(s.fullPlanRevenue)} · {formatPlanPercent(s.fullPlanMarginPercent)} margin
        {s.fixedPriceBurn !== null &&
          (s.fixedPriceBurn.burnPercent !== undefined
            ? ` · Fixed Price: ${formatPlanPercent(s.fixedPriceBurn.burnPercent)} of the ${formatPlanMoney(s.fixedPriceBurn.contractedFee)} Contracted Fee spent${
                s.fixedPriceBurn.actualCost !== undefined
                  ? ` (${formatPlanMoney(s.fixedPriceBurn.actualCost)} cost to date)`
                  : ""
              }`
            : ` · Contracted Fee ${formatPlanMoney(s.fixedPriceBurn.contractedFee)}`)}
        {Number(s.outsideWindowHours) > 0 &&
          ` · ${formatPlanHours(s.outsideWindowHours)} h logged outside the plan's months, not compared`}
      </div>
    </div>
  );
}
